<script setup lang="ts">
import { ToastRoot, type ToastRootEmits, useForwardProps } from "reka-ui";
import { type HTMLAttributes, computed } from "vue";
import { toastVariants } from "./index";
import { cn } from "@/lib/utils";

interface Props {
  class?: HTMLAttributes["class"];
  variant?:
    | "default"
    | "destructive"
    | "success"
    | "warning"
    | "info"
    | "error";
  open?: boolean;
  defaultOpen?: boolean;
  duration?: number;
  onOpenChange?: (open: boolean) => void;
}

const props = defineProps<Props>();

const emit = defineEmits<ToastRootEmits>();

const delegatedProps = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, onOpenChange, ...delegated } = props;
  return delegated;
});

const forwardedProps = useForwardProps(delegatedProps);

function handleOpenChange(value: boolean) {
  emit("update:open", value);
  props.onOpenChange?.(value);
}
</script>

<template>
  <ToastRoot
    v-bind="forwardedProps"
    :class="cn(toastVariants({ variant }), props.class)"
    @update:open="handleOpenChange"
    @escape-key-down="(event: KeyboardEvent) => emit('escapeKeyDown', event)"
    @pause="emit('pause')"
    @resume="emit('resume')"
    @swipe-start="(event: any) => emit('swipeStart', event)"
    @swipe-move="(event: any) => emit('swipeMove', event)"
    @swipe-cancel="(event: any) => emit('swipeCancel', event)"
    @swipe-end="(event: any) => emit('swipeEnd', event)"
  >
    <slot />
  </ToastRoot>
</template>
